import { useEffect, useRef } from "react";

import {
	liveSessionEnabled,
	normalizeProethicaSessionResponse,
} from "@constants/demo";
import { getProethicaTestByCollectionId } from "@constants/demo/proethica-tests";
import { useSessionStore } from "@features/session";
import { sessionItemsQueryOptions } from "@features/session/api/queries";
import { resolveSummativeItemStates } from "@features/session/helpers/seed-answer-state";
import { useProethicaTestItemStatesStore } from "@features/session/hooks/useProethicaTestItemStatesStore";
import { flattenSessionItems } from "@helpers/session";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useQuery } from "@tanstack/react-query";
import { useLocalSearchParams } from "expo-router";

export const useProethicaReportSession = () => {
	const { id: urlSessionId } = useLocalSearchParams<{ id: string }>();
	const storeId = useSessionStore((state) => state.id);
	const storeNumericId = useSessionStore((state) => state.numericId);
	const hydrateReportSession = useSessionStore(
		(state) => state.hydrateReportSession,
	);
	const getTestItemStates = useProethicaTestItemStatesStore(
		(state) => state.getTestItemStates,
	);
	const currentExam = useDemoExamStore((state) => state.currentExam);

	const numericUrlId = Number(urlSessionId);

	// Baseline = store identity when this URL id first armed the hydrator.
	// Hydration is allowed only while the store still holds that baseline (or
	// already matches the URL): if another flow claims the store mid-fetch
	// (e.g. a new session created from this report's CTA), the late response
	// must not overwrite it. Unlike a plain "store differs from URL" check,
	// the baseline lets browser-back/direct-nav hydration proceed when the
	// store legitimately holds the previous screen's session.
	const baselineRef = useRef<{
		key: number;
		id: string;
		numericId: string;
	} | null>(null);
	// Hydration is once per URL id: after it runs, a CTA on this report can
	// resume the very session the baseline captured (pause pre-test -> module
	// report -> "Continue to Pre-Test"), making the store ids equal the baseline
	// again. Without this latch that false "unchanged since armed" re-hydrates
	// the module session over the just-resumed test and corrupts its app bar.
	const hydratedKeyRef = useRef<number | null>(null);
	if (baselineRef.current?.key !== numericUrlId) {
		const current = useSessionStore.getState();
		baselineRef.current = {
			key: numericUrlId,
			id: String(current.id),
			numericId: String(current.numericId),
		};
		hydratedKeyRef.current = null;
	}

	const isStale =
		Boolean(urlSessionId) &&
		String(urlSessionId) !== String(storeId) &&
		String(urlSessionId) !== String(storeNumericId);
	const enabled =
		liveSessionEnabled(currentExam?.id) && numericUrlId > 0 && isStale;

	const query = useQuery({
		...sessionItemsQueryOptions(numericUrlId),
		enabled,
	});

	const data = query.data;
	useEffect(() => {
		if (!enabled) {
			return;
		}
		if (!data) {
			return;
		}
		if (hydratedKeyRef.current === numericUrlId) {
			return;
		}
		const current = useSessionStore.getState();
		const baseline = baselineRef.current;
		const matchesUrl =
			String(numericUrlId) === String(current.id) ||
			String(numericUrlId) === String(current.numericId);
		const unchangedSinceArmed =
			baseline != null &&
			String(current.id) === baseline.id &&
			String(current.numericId) === baseline.numericId;
		if (!(matchesUrl || unchangedSinceArmed)) {
			return;
		}
		const response = normalizeProethicaSessionResponse(data);
		const test = getProethicaTestByCollectionId(response.collectionId);
		const moduleId =
			response.collectionId != null ? String(response.collectionId) : undefined;
		const sessionItems = flattenSessionItems(response.sessionItems);
		const itemStates = test
			? resolveSummativeItemStates(
					sessionItems,
					getTestItemStates(numericUrlId),
				)
			: undefined;
		hydrateReportSession({
			id: response.id,
			numericId: numericUrlId,
			sessionItems,
			moduleId,
			moduleSummary: response.moduleSummary,
			proethicaTestKind: test?.kind,
			// Keep the summative flag in lockstep with the hydrated session: the
			// store may still carry `isSummativeTest: true` from a paused test, and
			// leaving it set against module items renders the wrong session app bar.
			isSummativeTest: test != null,
			itemStates,
		});
		hydratedKeyRef.current = numericUrlId;
	}, [enabled, data, numericUrlId, hydrateReportSession, getTestItemStates]);

	return {
		hydrating: enabled && !query.isError,
		hydrateError: query.isError ? query.error : null,
		retryHydrate: query.refetch,
	};
};
